'use client';

import { useTranslation } from '@/i18n/LanguageProvider';
import { useState, useEffect, useCallback } from 'react';
import Link from '@/components/LocaleLink';
import { X, Settings, Shield, ChevronDown, ChevronUp, Cookie } from 'lucide-react';

// ==================== TYPES ====================

export interface CookiePreferences {
  necessary: boolean;   // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  consentDate: string;
  consentVersion: string;
}

const CONSENT_KEY = 'iterogo_cookie_consent';
const CONSENT_VERSION = '1.0';

// ==================== HELPERS ====================

export function getCookiePreferences(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    const prefs = JSON.parse(stored) as CookiePreferences;
    // Check version
    if (prefs.consentVersion !== CONSENT_VERSION) return null;
    return prefs;
  } catch {
    return null;
  }
}

export function saveCookiePreferences(prefs: CookiePreferences): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
  // Dispatch event so other components can react
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: prefs }));
}

export function hasAnalyticsConsent(): boolean {
  const prefs = getCookiePreferences();
  return prefs?.analytics === true;
}

export function hasMarketingConsent(): boolean {
  const prefs = getCookiePreferences();
  return prefs?.marketing === true;
}

// ==================== COOKIE CATEGORY INFO ====================

const categories = [
  {
    id: 'necessary' as const,
    name: 'Cookies necesarias',
    description: 'Imprescindibles para el funcionamiento del sitio web. Incluyen cookies de sesión, autenticación y preferencias básicas. No se pueden desactivar.',
    required: true,
    cookies: [
      { name: 'firebase_auth', purpose: 'Autenticación de usuario', duration: 'Sesión' },
      { name: 'iterogo_cookie_consent', purpose: 'Guardar tus preferencias de cookies', duration: '12 meses' },
    ],
  },
  {
    id: 'analytics' as const,
    name: 'Cookies analíticas',
    description: 'Nos permiten entender cómo usas el sitio web para mejorar tu experiencia. Recopilan información de forma anónima sobre las páginas visitadas y el comportamiento de navegación.',
    required: false,
    cookies: [
      { name: '_ga / _ga_*', purpose: 'Google Analytics: análisis de tráfico y comportamiento', duration: '2 años' },
    ],
  },
  {
    id: 'marketing' as const,
    name: 'Cookies de marketing',
    description: 'Utilizadas para mostrar anuncios relevantes y medir la eficacia de campañas publicitarias. Pueden ser establecidas por terceros.',
    required: false,
    cookies: [
      { name: '_fbp', purpose: 'Facebook Pixel: seguimiento de conversiones', duration: '3 meses' },
      { name: '_gcl_au', purpose: 'Google Ads: atribución de conversiones', duration: '3 meses' },
    ],
  },
];

// ==================== COMPONENT ====================

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    consentDate: '',
    consentVersion: CONSENT_VERSION,
  });
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  useEffect(() => {
    // Small delay to avoid flash on page load
    const timer = setTimeout(() => {
      const existing = getCookiePreferences();
      if (!existing) {
        setVisible(true);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const prefs: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      consentDate: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    saveCookiePreferences(prefs);
    setVisible(false);
    setShowSettings(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const prefs: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      consentDate: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    saveCookiePreferences(prefs);
    setVisible(false);
    setShowSettings(false);
    // Remove existing analytics/marketing cookies
    removeNonEssentialCookies();
  }, []);

  const handleSavePreferences = useCallback(() => {
    const prefs: CookiePreferences = {
      ...preferences,
      necessary: true,
      consentDate: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    saveCookiePreferences(prefs);
    setVisible(false);
    setShowSettings(false);
    if (!prefs.analytics || !prefs.marketing) {
      removeNonEssentialCookies();
    }
  }, [preferences]);

  const removeNonEssentialCookies = () => {
    // Remove GA cookies
    document.cookie.split(';').forEach(c => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_fbp') || name.startsWith('_gcl')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      }
    });
  };

  const toggleCategory = (id: 'analytics' | 'marketing') => {
    setPreferences(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (!visible && !showSettings) {
    // Floating button to reopen settings
    return (
      <button
        onClick={() => { setShowSettings(true); setVisible(true); }}
        className="fixed bottom-4 left-4 z-50 bg-white dark:bg-gray-800 shadow-lg rounded-full p-3 hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700 group"
        title={t('cookieConsent.configure')}
        aria-label={t('cookieConsent.configure')}
      >
        <Cookie className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-primary transition-colors" />
      </button>
    );
  }

  return (
    <>
      {/* Overlay */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-[998] backdrop-blur-sm" onClick={() => { setShowSettings(false); if (!getCookiePreferences()) setVisible(true); else setVisible(false); }} />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col animate-in">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 dark:text-gray-100">{t('cookieConsent.configure')}</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Gestiona tus preferencias de privacidad</p>
                </div>
              </div>
              <button onClick={() => { setShowSettings(false); if (!getCookiePreferences()) setVisible(true); else setVisible(false); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </button>
            </div>

            {/* Categories */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Utilizamos cookies para mejorar tu experiencia en nuestro sitio web. Puedes elegir qué categorías de cookies aceptar. Para más información, consulta nuestra{' '}
                <Link href="/cookies" className="text-primary hover:underline">política de cookies</Link> y nuestra{' '}
                <Link href="/privacy" className="text-primary hover:underline">política de privacidad</Link>.
              </p>

              {categories.map((cat) => (
                <div key={cat.id} className="border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between p-4">
                    <button
                      onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                      className="flex items-center gap-3 flex-1 text-left"
                    >
                      {expandedCategory === cat.id ? (
                        <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{cat.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{cat.cookies.length} cookie{cat.cookies.length > 1 ? 's' : ''}</p>
                      </div>
                    </button>

                    {cat.required ? (
                      <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full font-medium">Siempre activas</span>
                    ) : (
                      <button
                        onClick={() => toggleCategory(cat.id as 'analytics' | 'marketing')}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          preferences[cat.id] ? 'bg-primary' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            preferences[cat.id] ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    )}
                  </div>

                  {expandedCategory === cat.id && (
                    <div className="px-4 pb-4 border-t bg-gray-50 dark:bg-gray-900">
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-3 mb-3">{cat.description}</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b dark:border-gray-700">
                            <th className="text-left py-1.5 font-medium text-gray-700 dark:text-gray-300">Cookie</th>
                            <th className="text-left py-1.5 font-medium text-gray-700 dark:text-gray-300">Finalidad</th>
                            <th className="text-left py-1.5 font-medium text-gray-700 dark:text-gray-300">Duración</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cat.cookies.map((cookie) => (
                            <tr key={cookie.name} className="border-b dark:border-gray-700 last:border-0">
                              <td className="py-1.5 font-mono text-gray-500 dark:text-gray-400">{cookie.name}</td>
                              <td className="py-1.5 text-gray-600 dark:text-gray-300">{cookie.purpose}</td>
                              <td className="py-1.5 text-gray-500 dark:text-gray-400">{cookie.duration}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-5 border-t bg-gray-50 dark:bg-gray-900 flex flex-col sm:flex-row gap-2">
              <button onClick={handleRejectAll} className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                Rechazar opcionales
              </button>
              <button onClick={handleSavePreferences} className="flex-1 px-4 py-2.5 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary-50 transition-colors">
                Guardar selección
              </button>
              <button onClick={handleAcceptAll} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
                Aceptar todas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner */}
      {visible && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-[997] p-4">
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-1">{t('cookieConsent.title')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Utilizamos cookies para mejorar tu experiencia, analizar el tráfico del sitio y personalizar contenido. 
                    Puedes aceptar todas las cookies, rechazar las opcionales o configurar tus preferencias. 
                    Consulta nuestra <Link href="/cookies" className="text-primary hover:underline">política de cookies</Link> y <Link href="/privacy" className="text-primary hover:underline">política de privacidad</Link> para más información.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    <button onClick={handleRejectAll} className="px-5 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      Rechazar opcionales
                    </button>
                    <button onClick={() => setShowSettings(true)} className="px-5 py-2.5 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary-50 transition-colors flex items-center justify-center gap-2">
                      <Settings className="w-4 h-4" /> Configurar
                    </button>
                    <button onClick={handleAcceptAll} className="px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
                      Aceptar todas
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
